import { JSX } from 'preact'
import { useCallback } from 'preact/hooks'
import classnames from 'classnames'
import { ActionType, BlockType, TBlockProps } from '@gsc/types'
import { previewProps } from '../../preview'
import { useTemplateTags } from '../useTemplateTags'
import { useActions } from '../useActions'
import { useComponentStyle } from '../Style'

function findLinkElement(node: Node): HTMLLinkElement | null {
  while (node && (!node.nodeName || node.nodeName.toLowerCase() !== 'a')) {
    node = node.parentNode
  }
  return node as HTMLLinkElement
}

export function useLinkClickHandler(): (e: MouseEvent) => void {
  const processActions = useActions()
  return useCallback(
    function handleLinkClick(e: MouseEvent) {
      // Ignore canceled events and right clicks
      if (e.defaultPrevented || e.button !== 0) {
        return
      }

      const el = findLinkElement(e.target as Node)
      if (!el) {
        return
      }

      // Prevent :focus from sticking; preventDefault() stops blur in some browsers
      el.blur()
      e.preventDefault()
      e.stopPropagation()

      return processActions([
        {
          type: ActionType.OpenUrl,
          url: el.href,
          target: (el.target as '_blank' | '_self') || '_self',
          label: el.innerText,
        },
      ])
    },
    [processActions],
  )
}

export const Text = ({ text, role, tag, ...props }: TBlockProps<BlockType.Text>): JSX.Element => {
  const { template } = useTemplateTags()
  const handleLinkClick = useLinkClickHandler()
  const { classes } = useComponentStyle('text', props)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Tag = (tag || 'p') as any

  return text ? (
    <Tag
      className={classnames(classes)}
      onClick={handleLinkClick}
      dangerouslySetInnerHTML={{ __html: template(text) }}
      role={role}
      {...(props.id ? previewProps(BlockType.Text, props.id) : {})}
    />
  ) : null
}
